import React, { useState, useEffect } from 'react';
import brandConfig from '../../config/brandConfig';
import { Building2, MapPin, Users, Bell, ArrowRight, LayoutDashboard } from 'lucide-react';

const DualCTASection = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const checkAuth = () => {
      if (typeof window !== 'undefined') {
        const accessToken = localStorage.getItem('token');
        setIsLoggedIn(!!accessToken);
      }
    };
    checkAuth();
    window.addEventListener('storage', checkAuth);
    window.addEventListener('focus', checkAuth);
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('focus', checkAuth);
    };
  }, []);

  const dashboardUrl = brandConfig.dashboardUrl;

  return (
    <div className="py-20 lg:py-28 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14">
          <p className="text-sm font-bold uppercase tracking-[0.2em] mb-3" style={{ color: brandConfig.primaryColor }}>
            Empieza Ahora
          </p>
          <h2 className="text-3xl lg:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight">
            Que estas buscando?
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Ya sea que quieras vender o comprar, tenemos la solucion perfecta para ti
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">

          {/* Para Inmobiliarias */}
          <div className="group relative bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/20 rounded-3xl p-8 lg:p-10 border border-orange-100 dark:border-orange-900/30 card-hover overflow-hidden">
            {/* Decorative corner */}
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-10" style={{ background: brandConfig.primaryColor }} />

            <div className="relative">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300 bg-white p-2">
                <img
                  src="/images/android-chrome-192x192.png"
                  alt={brandConfig.getLogoAlt()}
                  width={64}
                  height={64}
                  className="rounded-xl"
                />
              </div>

              <h3 className="text-2xl lg:text-3xl font-extrabold mb-3 text-gray-900 dark:text-white tracking-tight">
                Soy Inmobiliaria
              </h3>

              <p className="text-base text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                Publica tus lotes y gestiona tus ventas con herramientas profesionales.
                Conecta con miles de compradores calificados.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: <Users className="w-4 h-4" />, text: 'Acceso a miles de compradores' },
                  { icon: <MapPin className="w-4 h-4" />, text: 'Visores interactivos avanzados' },
                  { icon: <Building2 className="w-4 h-4" />, text: 'Gestion completa de ventas' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: brandConfig.primaryColor }}>
                      {item.icon}
                    </div>
                    <span className="font-semibold text-gray-800 dark:text-white text-sm">{item.text}</span>
                  </div>
                ))}
              </div>

              {!isMounted ? null : isLoggedIn ? (
                <a href={`${dashboardUrl}/dashboard`} className="flex items-center justify-center gap-2 w-full text-white px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02]" style={{ background: brandConfig.getPrimaryGradient() }}>
                  <LayoutDashboard className="w-5 h-5" />
                  Ir a Dashboard
                  <ArrowRight className="w-5 h-5" />
                </a>
              ) : (
                <a href={`${dashboardUrl}/iniciar-sesion`} className="flex items-center justify-center gap-2 w-full text-white px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:scale-[1.02]" style={{ background: brandConfig.getPrimaryGradient() }}>
                  <Building2 className="w-5 h-5" />
                  Publicar Mis Lotes
                  <ArrowRight className="w-5 h-5" />
                </a>
              )}

              <p className="text-xs mt-4 text-center text-gray-400 dark:text-gray-500 font-medium">
                Registro gratuito &bull; Sin compromiso &bull; Soporte incluido
              </p>
            </div>
          </div>

          {/* Para Clientes */}
          <div className="group relative bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/20 rounded-3xl p-8 lg:p-10 border border-blue-100 dark:border-blue-900/30 card-hover overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-10 bg-blue-500" />

            <div className="relative">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #102a43 0%, #243b53 100%)' }}>
                <Bell className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-2xl lg:text-3xl font-extrabold mb-3 text-gray-900 dark:text-white tracking-tight">
                Busco mi lote ideal
              </h3>

              <p className="text-base text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                Suscribete para recibir notificaciones de nuevos terrenos en tu region.
                Encuentra la propiedad perfecta antes que otros.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: <Bell className="w-4 h-4" />, text: 'Alertas instantaneas' },
                  { icon: <MapPin className="w-4 h-4" />, text: 'Solo en tu region' },
                  { icon: <Users className="w-4 h-4" />, text: 'Acceso prioritario' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-900 text-white">
                      {item.icon}
                    </div>
                    <span className="font-semibold text-gray-800 dark:text-white text-sm">{item.text}</span>
                  </div>
                ))}
              </div>

              <button className="flex items-center justify-center gap-2 w-full text-white px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 shadow-lg shadow-blue-900/25 hover:shadow-blue-900/40 hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg, #102a43 0%, #243b53 100%)' }}>
                <Bell className="w-5 h-5" />
                Suscribirme para Terrenos
                <ArrowRight className="w-5 h-5" />
              </button>

              <p className="text-xs mt-4 text-center text-gray-400 dark:text-gray-500 font-medium">
                Gratis &bull; Sin spam &bull; Cancela cuando quieras
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="text-center mt-14">
          <div className="inline-flex items-center gap-4">
            <div className="h-px w-20 bg-gradient-to-r from-transparent to-gray-200 dark:to-gray-700"></div>
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500">
              O explora nuestras propiedades disponibles
            </span>
            <div className="h-px w-20 bg-gradient-to-l from-transparent to-gray-200 dark:to-gray-700"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DualCTASection;
